<template>
  <div>
    <div class="saas-blog-post">
      <!--- HEADER -->
      <div class="saas-blog-post__header">
        <div class="saas-blog-post__header-inner">
          <div class="saas-blog-post__left-column">
            <ButtonIcon
              tag="nuxt-link"
              :to="{ name: 'blog' }"
              icon="iconoir-nav-arrow-left"
              class="saas-blog-post__back-button"
            >
            </ButtonIcon>
          </div>
          <div class="saas-blog-post__content-column">
            <h1 class="saas-blog-post__title">{{ blogPost.title }}</h1>
            <div class="saas-blog-post__info">
              {{ formatBlogPostDate(blogPost.date) }}
              <span class="saas-blog-post__info-dot">•</span>
              {{ blogPost.author }}
            </div>
          </div>
        </div>

        <div
          class="saas-rounded-shape saas-rounded-shape__default-right-1"
        ></div>
        <div
          class="saas-rounded-shape saas-rounded-shape__default-right-2"
        ></div>
      </div>

      <div class="saas-blog-post__wrapper">
        <div class="saas-blog-post__left-column">
          <div class="saas-blog-post__column-sticky-content">
            <div class="saas-blog-post__socials">
              <ul class="saas-blog-post__social-links">
                <li>
                  <a
                    :href="`http://twitter.com/share?url=${fullUrl}`"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="saas-blog-post__social-link"
                  >
                    <i class="baserow-icon-twitter"></i>
                  </a>
                </li>
                <li>
                  <a
                    :href="`https://www.linkedin.com/shareArticle?url=${fullUrl}`"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="saas-blog-post__social-link"
                  >
                    <i class="baserow-icon-linkedin"></i>
                  </a>
                </li>
                <li>
                  <a
                    :href="`https://www.facebook.com/sharer.php?u=${fullUrl}`"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="saas-blog-post__social-link"
                  >
                    <i class="baserow-icon-facebook"></i>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div class="saas-blog-post__content-column">
          <div class="saas-blog-post__intro-image">
            <img
              v-if="!blogPost.image"
              src="@saas/assets/images/blog_image_placeholder.png"
              alt="Blog placeholder image"
            />
            <img v-else :src="blogPost.image" :alt="blogPost.image_alt" />
          </div>
          <div class="saas-blog-post__content" v-html="html"></div>
          <div
            v-if="blogPost.author_description"
            class="post-author margin-top-4"
          >
            <div class="post-author__inner">
              <div v-if="blogPost.author_photo" class="post-author__image">
                <img :src="blogPost.author_photo" alt="" />
              </div>
              <div>
                <div class="post-author__title">{{ blogPost.author }}</div>
                <p class="post-author__content">
                  {{ blogPost.author_description }}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="saas-blog-post__right-column">
          <div class="saas-blog-post__column-sticky-content">
            <ul class="saas-blog-post__nav-items">
              <li class="saas-blog-post__nav-item">
                <span class="saas-blog-post__nav-item-link">On this page</span>
                <nav class="saas-blog-post__nav-sub-items">
                  <li
                    v-for="section in toc"
                    :key="section.id"
                    class="saas-blog-post__nav-sub-item"
                    :class="{
                      'saas-blog-post__nav-sub-item--read':
                        readSections.includes(section.id),
                      'saas-blog-post__nav-sub-item--active':
                        readSections[readSections.length - 1] === section.id,
                    }"
                  >
                    <a
                      :href="`#${section.id}`"
                      class="saas-blog-post__nav-sub-item-link"
                      >{{ section.name }}</a
                    >
                  </li>
                </nav>
              </li>
            </ul>

            <SignupCard></SignupCard>
          </div>
        </div>
      </div>
    </div>
    <BlogPostsHighlightedSection></BlogPostsHighlightedSection>
  </div>
</template>

<script>
import { BlogMarkdownPage } from '@saas/markdown/blogMarkdownPage'
import moment from '@baserow/modules/core/moment'
import BlogPostsHighlightedSection from '@saas/components/blog/BlogPostsHighlightedSection.vue'
import SignupCard from '@saas/components/SignupCard.vue'

export default {
  components: { SignupCard, BlogPostsHighlightedSection },
  props: {
    blogPost: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      readSections: [],
      toc: [],
      html: '',
    }
  },
  computed: {
    fullUrl() {
      return this.$config.PUBLIC_WEB_FRONTEND_URL + this.$route.fullPath
    },
  },
  created() {
    const markdownPage = new BlogMarkdownPage(
      '/blog/' + this.blogPost.slug,
      this.blogPost.content,
      this.$config
    )
    this.toc = markdownPage.toc
    this.html = markdownPage.html
  },
  mounted() {
    this.$nextTick(() => {
      window.addEventListener('scroll', this.updateSectionTOC, true)
      window.addEventListener('resize', this.updateSectionTOC)
      this.$once('hook:beforeDestroy', () => {
        window.removeEventListener('scroll', this.updateSectionTOC, true)
        window.removeEventListener('resize', this.updateSectionTOC)
      })
    })
  },
  methods: {
    formatBlogPostDate(date) {
      return moment(date).format('MMMM D, YYYY')
    },
    updateSectionTOC() {
      const sections = this.$el.querySelectorAll('*[id]')
      const readSections = []
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i]
        const rect = section.getBoundingClientRect()
        const id = section.getAttribute('id')

        // As soon as the section enters the viewport, we add it to the list, so that
        // it can be shown as active in the right sidebar.
        if (rect.top < window.innerHeight) {
          readSections.push(id)
        }

        // If the section hasn't left the viewport, then we don't want to show the
        // next ones as active, so we break the loop.
        if (rect.top > 0) {
          break
        }
      }
      this.readSections = readSections
    },
  },
}
</script>
